import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

/**
 * 多 Tab 状态管理。
 *
 * <h2>设计</h2>
 * 侧边栏点一项 → 开一个 tab；tab 的 **key 是侧边栏一级路径**（如 `/orders`）。
 * ⚠️ 子页面（如订单详情 `/orders/123`）**不另开 tab** ——
 *    它复用所属一级 tab，tab 标题随当前路由变化。
 *    这是为了避免「从订单列表点 10 个详情就爆 10 个 tab」。
 *
 * <h2>🔴 key 怎么定 —— `pathToTabKey`</h2>
 * 路由 path 不一定等于 tab key：`/orders/123` 的 tab key 是 `/orders`。
 * ⇒ 显式维护一张「一级路径」白名单（正好就是侧边栏那 9 项 + 运营那 1 项），
 *    路由进来自动折回到它所属的那一条。匹配不到的（比如 `/` 重定向）退回工作台。
 *
 * <h2>刷新页面</h2>
 * ⛔ **不持久化**：刷新后只剩工作台。理由是路由与 tab 数组对不齐（tab 持的 fullPath
 *    可能已失效），持久化只会带来「关不掉的空 tab」。工作台是默认首页，单开它即可。
 *
 * <h2>🔴 与 keep-alive 的关系（DESIGN §12 外壳 A 案）</h2>
 * 「切 tab 回来时筛选/滚动/已加载数据都还在」靠 `AppShell` 的 `<KeepAlive>` 实现，
 * 而**缓存名单就是这个 store 推导的**（{@link cacheableComponentNames}）：
 * 「打开中的页签 × 页签白名单内的路由」决定哪些页面被缓存。⇒ 关闭页签 = 名单收缩 =
 * 缓存被驱逐，⛔ 不需要第二份需要人肉同步的缓存清单。
 */

export interface TabItem {
  /** 侧边栏一级路径，如 `/orders`。同一 key 只有一个 tab */
  key: string
  /** 标题：跟随当前路由的 meta.title 变化（子页面会变） */
  title: string
  /** 当前 tab 里正在显示的路由全路径（含 query）—— 切 tab 时 router.push 到它 */
  fullPath: string
}

/** 🔴 侧边栏一级路径白名单 —— 与 AppShell.vue 的菜单项一一对应 */
export const TOP_LEVEL_PATHS = [
  '/workbench',
  '/intelligence',
  '/assistant',
  '/orders',
  '/returns',

  // B10：订单数据大屏（订单组第二个一级页；与 /reports/sales 销售大屏分开）
  '/datascreen',

  // 🔴 订单消息（B7）：模板列表与发送记录是两个独立页（竞品同位），各占一个 tab。
  //    ⚠️ '/messages/records' 必须登记 —— pathToTabKey 先精确命中它，否则会被前缀折回 '/messages'。
  '/messages',
  '/messages/records',
  '/products',
  '/logistics',
  // 上架记录（竞品「商品→上架记录」同位，f-070）—— 占位页第三版换真页，tab 键现在就定下
  '/listing/records',
  '/collection',
  '/selection/overview',
  '/selection/categories',
  '/selection/rankings',
  '/selection/tags',
  '/selection/analytics',
  '/sourcing/1688',
  // B9：1688 账号授权（与选品铺货同族的两页，各自一级 tab）
  '/sourcing/1688/auth',
  '/reports/sales',
  // 🔴 促销（B8 三页）：/promotions（活动列表）、/promotions/products、/promotions/add
  //    三个路由都折回这一个 tab —— 活动列表 → 促销中商品 → 添加商品是一条流程，不该爆三个页签
  //    （同 /selection 范式）。⚠️ '/phase2/auto-promotion-removal' 仍是独立 tab。
  '/promotions',
  '/phase2/auto-promotion-removal',
  '/phase2/advanced-messaging',
  // 🔴 选品（二期 T10）：/selection/new、/selection/tasks、/selection/tasks/:id/preview
  //    三个路由都折回这一个 tab —— 向导 → 预演结果是一条流程，不该爆三个页签。
  //    ⚠️ '/selection' 本身不是路由：syncFromRoute 会把实际 fullPath 记进 tab，
  //    点 tab 回到的是最后停留的那一页。
  '/selection',
  '/shops',
  // 店铺分组（竞品 batch-02 对标）—— 店铺组的第二个一级页
  '/shop-groups',
  // 水印此前漏登记：点「水印」菜单 tab 会折回 /workbench（foldTab 兜底）—— 顺手修正
  '/watermarks',
  '/pricing',
  // 定价工具/利润计算器（FE-5）：工具组两个菜单入口同页双页签，?tab= 直达页签但只开一个 tab
  '/tools/pricing',
  '/wallet',
  '/billing',
  '/settings',
  '/help',
  '/ops/recharges',
  // 🔀 运营配置组（ops-config-tabs-v1.2）：组 path 为唯一页签身份（?tab 切换不新开页签）；
  //    旧配置路由已前端重定向到组 URL，白名单不再保留旧 key（shipping-rates 原本就未登记）。
  '/ops/config/access',
  '/ops/config/fees',
  '/ops/config/system',
  '/ops/reports',
  '/ops/returns',
  '/ops/forwarder-settlements',
  '/ops/forwarders',
  '/ops/tenants',
  '/ops/register-seller',
  // 佣金费率表（市场分析类目×价格档，与 fee-profiles 定价费率是两张表）
  '/ops/price-plans',
  '/ops/subscriptions',
  '/ops/entitlements',
  '/ops/unit-economics',
  '/ops/commercial-extended',
  '/notifications',
  // 🔴 货代桌面后台（doc/59 §零：货代管理后台复用 seller）—— 走多 Tab，与运营 / 卖家后台同型
  '/fwd/settlement',
  '/fwd/payee',
] as const

/** 🔴 不可关闭的 tab —— 工作台是首页，必须常驻 */
const PERSISTENT_KEYS = new Set<string>(['/workbench'])

/** 最多同时打开的 tab 数 —— 防止失控 */
const MAX_TABS = 10

/**
 * 🔴 keep-alive 缓存条数上限（`:max` 兜底，§12 A 案「增量可回退」的保险丝）。
 *
 * 为什么是 `MAX_TABS × 2`：缓存是**按路由 path** 存的（`/orders` 与 `/orders/123`
 * 是两条），一个页签最常驻的正是「列表 + 详情」这两条。取 10 × 2 = 20 让
 * **满页签时不会因为 max 把用户还开着的页签挤掉**（被 max 淘汰的缓存会丢状态，
 * 那是真的丢东西，不是「省点内存」）。它只是兜底：正常使用远达不到这个数。
 */
export const MAX_CACHED_VIEWS = MAX_TABS * 2

function isPersistent(key: string): boolean {
  return PERSISTENT_KEYS.has(key)
}

/**
 * 把 path 折回它所属的**一级白名单路径**；命中不到返回 `null`。
 *
 * ⚠️ 与 {@link pathToTabKey} 的区别只有一个：**这里不兜底**。
 * `pathToTabKey('/login')` 会退回 `/workbench`（那是给「tab 归属」用的兜底），
 * 而 `/login` 本身**不是**页签页 —— 判断「这一页属不属于页签体系」时必须用
 * 这个函数（`!== null`），⛔ 用 pathToTabKey 的兜底值判会把登录页也算成工作台。
 */
export function matchTopLevelPath(path: string): string | null {
  // 先精确命中（`/ops/recharges` 这类多段路径需要精确匹配，不能被前缀逻辑吃掉）
  if ((TOP_LEVEL_PATHS as readonly string[]).includes(path)) return path
  // 再按前缀折回：取 path 第一段拼成 `/xxx`，看它在不在白名单
  const seg = '/' + (path.replace(/^\/+/, '').split('/')[0] ?? '')
  return (TOP_LEVEL_PATHS as readonly string[]).includes(seg) ? seg : null
}

/**
 * 把任意路由 path 折回它所属的**一级 tab key**。
 *
 * 例：`/orders/123` → `/orders`；`/shops/5` → `/shops`；`/workbench` → `/workbench`。
 * 命中不到一级路径（如纯数字段、未登记的新页）退回 `/workbench`。
 */
export function pathToTabKey(path: string): string {
  return matchTopLevelPath(path) ?? '/workbench'
}

/**
 * 这一页是否属于**页签体系**（白名单内的一级页或其子页）。
 *
 * 🔴 keep-alive 的准入判据：只有这里为 true 的路由才允许被缓存。
 * 非页签路径（`/login` `/register` 这类 blank 布局页、货代 PWA `/tasks` 与 `/work/:id`、
 * `/fwd/sessions` `/ops/shipping-rates` 等未登记子页）一律**不缓存** ——
 * 它们的行为与加 keep-alive 之前**完全一致**。
 */
export function isTabbedPath(path: string): boolean {
  return matchTopLevelPath(path) !== null
}

/** `router.getRoutes()` 里我们真正要读的那两个字段（结构化类型，避免 store 依赖 vue-router 的完整记录类型） */
export interface CacheableRouteRecord {
  path: string
  components?: Record<string, unknown> | null
}

/**
 * 组件名：`<script setup>` 由文件名推断出 `__name`（本工程所有路由页都是 script setup），
 * 显式 `defineOptions({ name })` / Options API 组件则是 `name`。两者取到即用。
 *
 * 🔴🔴 **函数一律不算**（返回 null）—— 这是个会静默出错的坑：
 * 懒加载路由的 `components.default` 是个箭头函数，而 ES2015 的**函数名字推断**会给它
 * 一个"看起来很像名字"的 `name`：写在 `component: () => import(...)` 里就叫 `"component"`，
 * 写在 `components: { default: () => ... }` 里就叫 `"default"`。
 * 照收的话 include 里会混进 `"default"` 这种垃圾名，而真正的组件名要等导航解析完才出现。
 * ⚠️ 本工程的路由组件全是 SFC（= 对象），所以「只认对象」没有漏网之鱼。
 */
function componentNameOf(component: unknown): string | null {
  if (!component || typeof component !== 'object') return null
  const c = component as { name?: unknown; __name?: unknown }
  const name = typeof c.name === 'string' && c.name ? c.name : c.__name
  return typeof name === 'string' && name ? name : null
}

/**
 * 推导 keep-alive 的 `:include`（组件名列表）——**页签缓存名单的唯一事实源**。
 *
 * 规则：路由记录里 **既属于页签白名单、其页签又是打开状态** 的组件名。
 *
 * <h2>🔴 为什么是「名字」而不是路由 meta</h2>
 * `KeepAlive` 的 `include` 只认**组件名**（`getComponentName(vnode.type)`），不认 path、
 * 也不认路由 meta —— 这是 Vue 的既有约定，跳不过去。所以名单只能由「组件名」表达。
 * ⛔ 而逐个 view 写 `defineOptions({ name })` 会引入第二份需要人肉维护的名单，
 * 且和路由表脱钩（改了文件名就静默失效）。这里改成**从路由记录读**：
 * 路由记录里的 `components.default` 就是那个组件对象本身，名字（`__name`）由
 * SFC 编译期从文件名注入 —— 零维护、不会和路由表漂移。
 *
 * <h2>⚠️ 必须由调用方在「路由变化后」重算</h2>
 * vue-router 是**导航完成时**才把懒加载组件写回 `record.components.default` 的
 * （`extractComponentsGuards`：`record.components[name] = resolvedComponent`）。
 * 而 `getRoutes()` 不是响应式的 ⇒ 调用方（AppShell）必须把当前路由 fullPath
 * 也读进这个计算的依赖里，否则**第一次进某页时名字还没写回**，会把页面漏出缓存。
 *
 * <h2>驱逐 = 名单收缩</h2>
 * 打开中的页签集合一变（新建 / 关闭 / 关其它），名单立刻跟着变。`KeepAlive` 在
 * `include` 变化后会 `pruneCache` 掉不再匹配的条目（关闭页签 ⇒ 该页缓存被驱逐，
 * 重开是干净态）。⚠️ 唯一例外：被淘汰的恰好是**当前正在显示**那一条时，Vue 只
 * 重置它的 keep-alive 标记、不立即卸载（不能让屏幕上的东西凭空消失）——
 * 它会在下一次导航离开时真正卸载，语义仍是「重开是干净态」。
 */
export function cacheableComponentNames(
  records: readonly CacheableRouteRecord[],
  openTabKeys: readonly string[],
): string[] {
  const open = new Set(openTabKeys)
  const names: string[] = []
  for (const record of records) {
    if (!isTabbedPath(record.path)) continue
    if (!open.has(pathToTabKey(record.path))) continue
    const name = componentNameOf(record.components?.default)
    if (name && !names.includes(name)) names.push(name)
  }
  return names
}

export const useTabsStore = defineStore('seller-tabs', () => {
  /** 打开的 tab 列表 —— 工作台永远在 index 0 */
  const tabs = ref<TabItem[]>([
    { key: '/workbench', title: '今日工作台', fullPath: '/workbench' },
  ])

  /** 当前激活的 tab key */
  const activeKey = ref<string>('/workbench')

  function reset(): void {
    tabs.value = [{ key: '/workbench', title: '今日工作台', fullPath: '/workbench' }]
    activeKey.value = '/workbench'
  }

  /**
   * 按【路由变化】同步 tab 状态。`AppShell` watch `$route` 时调它。
   *
   * - 折回一级 key；
   * - tab 不存在则新建（受 `MAX_TABS` 限制，超限返回 false 由调用方提示）；
   * - tab 存在则更新它的 title / fullPath（子页面在同一 tab 内切换）；
   * - activeKey 跟到该 tab。
   *
   * @returns 是否成功（false = 超 MAX_TABS，调用方可弹提示）
   */
  function syncFromRoute(path: string, title: string, fullPath: string): boolean {
    const key = pathToTabKey(path)
    const found = tabs.value.find((t) => t.key === key)
    if (found) {
      found.title = title || found.title
      found.fullPath = fullPath
    } else {
      if (tabs.value.length >= MAX_TABS) return false
      tabs.value.push({ key, title: title || key, fullPath })
    }
    activeKey.value = key
    return true
  }

  /**
   * 由【侧边栏点击 / 主动打开】调用。打开（或激活）一个 tab。
   * 与 `syncFromRoute` 的差别：这里不需要 title（拿不到 meta），title 由随后的路由同步补上。
   */
  function open(path: string): boolean {
    const key = pathToTabKey(path)
    const found = tabs.value.find((t) => t.key === key)
    if (found) {
      found.fullPath = path
    } else {
      if (tabs.value.length >= MAX_TABS) return false
      tabs.value.push({ key, title: key, fullPath: path })
    }
    activeKey.value = key
    return true
  }

  /**
   * 关闭一个 tab。
   * - 🔴 工作台不可关（直接忽略）；
   * - 关的是当前激活 tab ⇒ 自动激活相邻 tab（优先右、其次左）；
   * - 🔴🔴 **同时驱逐该页的 keep-alive 缓存**：`cacheableNames()` 是从 `tabs` 推导的，
   *   数组里少了这一条 ⇒ `include` 收缩 ⇒ `KeepAlive` 立即 `pruneCache` 掉它的缓存条目。
   *   ⇒ 重新打开这个页签看到的是**干净页面**（旧筛选绝不留着）。
   * @returns 关闭成功后应激活的 tab key（供调用方 router.push），或 null 表示无需跳转。
   */
  function close(key: string): string | null {
    if (isPersistent(key)) return null
    const idx = tabs.value.findIndex((t) => t.key === key)
    if (idx === -1) return null
    tabs.value.splice(idx, 1)
    if (activeKey.value !== key) {
      // 关的不是当前 tab，激活态不变
      return null
    }
    // 关的是当前 tab —— 激活相邻（工作台常驻 ⇒ 数组恒非空）
    const next = tabs.value[idx] ?? tabs.value[idx - 1] ?? tabs.value[0]
    if (!next) return null
    activeKey.value = next.key
    return next.fullPath
  }

  /**
   * 关闭除**当前激活 tab 和工作台**以外的所有 tab。
   *
   * 🔴 工作台恒留（PERSISTENT）。当前激活 tab 也留 —— 否则「关其它」把人当前正在看的
   *    那页也关了，违反直觉。当前 tab 本就是工作台时，结果只剩工作台一项。
   * - 若当前激活 tab 被关掉了（理论上不会，因为它在保留集合里），则激活工作台。
   * - 🔴 被关掉的那些页签，其 keep-alive 缓存同样被驱逐（同 `close`，靠 `include` 收缩）。
   * @returns 关闭成功后应激活的 tab fullPath（供调用方 router.push），或 null 表示无需跳转。
   */
  function closeOthers(): string | null {
    // 保留集合：工作台（持久）+ 当前激活
    const keep = new Set<string>([...PERSISTENT_KEYS, activeKey.value])
    let changed = false
    // 🔴 倒序删，避免 splice 改变下标导致跳过相邻项
    for (let i = tabs.value.length - 1; i >= 0; i--) {
      const tab = tabs.value[i]
      if (!tab) continue
      const key = tab.key
      if (!keep.has(key) && !isPersistent(key)) {
        tabs.value.splice(i, 1)
        changed = true
      }
    }
    if (!changed) return null
    // 激活态要么本来就在保留集合里（不变），要么兜底回工作台
    const stillThere = tabs.value.some((t) => t.key === activeKey.value)
    if (!stillThere) {
      const wb = tabs.value.find((t) => isPersistent(t.key))
      if (!wb) return null
      activeKey.value = wb.key
      return wb.fullPath
    }
    return null
  }

  /** 当前激活 tab 对应的路由目标（切 tab 时 push 它） */
  function activeRoute(): RouteLocationRaw {
    const t = tabs.value.find((x) => x.key === activeKey.value)
    return t ? t.fullPath : '/workbench'
  }

  /**
   * 当前**应被 keep-alive 缓存**的组件名（`:include` 的唯一来源）——名单只此一处。
   *
   * 传进来的路由记录由调用方从 `router.getRoutes()` 拿（store 不依赖 vue-router 实例）；
   * 页签这一侧（哪些页签开着）由 store 决定 ⇒ 关闭页签即等于「该页移出缓存名单」。
   * 见 {@link cacheableComponentNames}。
   */
  function cacheableNames(records: readonly CacheableRouteRecord[]): string[] {
    return cacheableComponentNames(records, tabs.value.map((t) => t.key))
  }

  return {
    tabs,
    activeKey,
    reset,
    syncFromRoute,
    open,
    close,
    closeOthers,
    activeRoute,
    isPersistent,
    cacheableNames,
  }
})
